import type {AnyObject} from 'typings/portaro.fe.types';

export type GoToActionFunction = (path: string) => Promise<void>;
export type ActionRequestActionFunction = (path: string, requestMethod: string, requestBody: string) => Promise<any>;
export type MappedActionFunction = (action: MappedAction) => Promise<any>;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface DefinedAction {
}

export interface GoToAction extends DefinedAction {
    path: string;
}

export interface ActionRequestAction extends DefinedAction {
    path: string;
    requestMethod: string;
    requestBody: string;
}

export interface MappedAction extends DefinedAction {
    actionType: string;
    params: AnyObject;
}

export function isActionRequestMenuItemAction(action: DefinedAction): action is ActionRequestAction {
    return (action as ActionRequestAction).path !== undefined && (action as ActionRequestAction).requestMethod !== undefined;
}

export function isGoToMenuItemAction(action: DefinedAction): action is GoToAction {
    return (action as GoToAction).path !== undefined && !isActionRequestMenuItemAction(action);
}

export function isMappedMenuItemAction(action: DefinedAction): action is MappedAction {
    return (action as MappedAction).actionType !== undefined;
}

export default class DefinedActionPerformer {
    public static readonly serviceName = 'definedActionPerformer';

    // eslint-disable-next-line no-empty,no-empty-function,@typescript-eslint/no-empty-function,@typescript-eslint/require-await
    private goToActionFunction: GoToActionFunction = async () => null;

    // eslint-disable-next-line @typescript-eslint/require-await
    private actionRequestActionFunction: ActionRequestActionFunction = async () => {
        throw new Error('actionRequestActionFunction is not registered. Register it please.');
    }

    private mappedActionFunctions: Map<string, MappedActionFunction> = new Map<string, MappedActionFunction>();

    public async performAction(action: DefinedAction): Promise<any> {
        if (isGoToMenuItemAction(action)) {
            return this.performGoToAction(action);
        }

        if (isActionRequestMenuItemAction(action)) {
            return this.performActionRequestAction(action);
        }

        if (isMappedMenuItemAction(action)) {
            return this.performMappedAction(action);
        }

        throw new Error(`DefinedAction ${action} is not supported`);
    }

    public registerMappedAction(actionType: string, actionFunction: MappedActionFunction) {
        this.mappedActionFunctions.set(actionType, actionFunction);
    }

    public registerGoToAction(actionFunction: GoToActionFunction) {
        this.goToActionFunction = actionFunction;
    }

    public registerActionRequestAction(actionFunction: ActionRequestActionFunction) {
        this.actionRequestActionFunction = actionFunction;
    }

    private async performGoToAction(action: GoToAction) {
        return this.goToActionFunction(action.path);
    }

    private async performActionRequestAction(action: ActionRequestAction) {
        return this.actionRequestActionFunction(action.path, action.requestMethod, action.requestBody);
    }

    private async performMappedAction(action: MappedAction) {
        const actionType: string = action.actionType;

        if (!this.mappedActionFunctions.has(actionType)) {
            throw new Error(`Action of type ${actionType} is not registered. Please, register it first in ${DefinedActionPerformer.serviceName}`);
        }

        const actionFunction = this.mappedActionFunctions.get(actionType);
        return actionFunction(action);
    }

}
